import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

/**
 * One-time-passcode input rendered as N centered boxes (one digit each).
 * - Typing a digit advances focus to the next box.
 * - Backspace on an empty box jumps focus to the previous box and clears it.
 * - Pasting a numeric string fills consecutive boxes from the focused index
 *   (so the user can paste the whole code into the first box).
 * - Strips non-digit characters defensively so password managers / autofill
 *   that include separators (`123 456`) still work.
 */
export function OTPCodeInput({
  length = 8,
  value,
  onChange,
  onComplete,
  autoFocus = true,
  disabled = false,
  ariaLabel = 'Sign-in code',
  testId = 'otp-input',
}: {
  length?: number;
  value: string;
  onChange: (next: string) => void;
  onComplete?: (next: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  testId?: string;
}) {
  const inputs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus && inputs.current[0]) inputs.current[0].focus();
  }, [autoFocus]);

  function setDigit(index: number, raw: string) {
    const digit = raw.replace(/\D/g, '').slice(0, 1);
    const arr = (value + '').padEnd(length, ' ').slice(0, length).split('');
    arr[index] = digit || ' ';
    const next = arr.join('').replace(/\s+$/, '');
    onChange(next);
    if (digit) {
      const nextIdx = Math.min(index + 1, length - 1);
      inputs.current[nextIdx]?.focus();
      inputs.current[nextIdx]?.select();
      const filled = next.replace(/\s/g, '');
      if (filled.length === length && onComplete) onComplete(filled);
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace') {
      const arr = (value + '').padEnd(length, ' ').slice(0, length).split('');
      if (!arr[index] || arr[index] === ' ') {
        // Empty cell → step back and clear.
        if (index > 0) {
          arr[index - 1] = ' ';
          onChange(arr.join('').replace(/\s+$/, ''));
          inputs.current[index - 1]?.focus();
          inputs.current[index - 1]?.select();
          e.preventDefault();
        }
      } else {
        arr[index] = ' ';
        onChange(arr.join('').replace(/\s+$/, ''));
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputs.current[index - 1]?.focus();
      inputs.current[index - 1]?.select();
      e.preventDefault();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputs.current[index + 1]?.focus();
      inputs.current[index + 1]?.select();
      e.preventDefault();
    }
  }

  function handlePaste(index: number, e: ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, length - index);
    if (!pasted) return;
    e.preventDefault();
    const arr = (value + '').padEnd(length, ' ').slice(0, length).split('');
    for (let i = 0; i < pasted.length; i++) arr[index + i] = pasted[i];
    const next = arr.join('').replace(/\s+$/, '');
    onChange(next);
    const focusIdx = Math.min(index + pasted.length, length - 1);
    inputs.current[focusIdx]?.focus();
    inputs.current[focusIdx]?.select();
    if (next.length === length && onComplete) onComplete(next);
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      // Single row, never wraps. On narrow viewports the boxes shrink and the
      // gap collapses so the 8 cells always fit edge-to-edge in the available
      // width — no orphan box dropping to a second line.
      className="mt-3 flex w-full min-w-0 flex-nowrap justify-center gap-1.5 sm:gap-2"
      data-testid={testId}
    >
      {Array.from({ length }).map((_, i) => {
        const ch = value[i] ?? '';
        return (
          <input
            key={i}
            ref={(el) => {
              inputs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            pattern="\d"
            maxLength={1}
            disabled={disabled}
            value={ch === ' ' ? '' : ch}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={(e) => handlePaste(i, e)}
            aria-label={`Digit ${i + 1} of ${length}`}
            data-testid={`${testId}-${i}`}
            className="h-11 min-w-0 flex-1 basis-0 rounded-md border border-stone-500/70 bg-stone-800 p-0 text-center font-mono text-lg tracking-widest text-bone shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/60 disabled:bg-stone-900 disabled:text-stone-500 sm:h-14 sm:text-2xl"
          />
        );
      })}
    </div>
  );
}
